import { TextOverrides } from './content-overrides.types';
import { CustomPage } from './custom-page.types';
import { IScheduledEmailCustomizations, IScheduledEmailSettings } from './email.types';
import { Module, PageModule } from './module.types';
import { Session } from './session.types';
import { TranslateString } from './translation.types';
import { MagicLink } from '@/micros/edit-project/grid-areas/main/audiences/types';

export interface Registration {
  type: RegistrationTypes;
  enableSocial: boolean;
  singleSignOn: SingleSignOnSettings;
  sessionReminders: boolean;
  registrationConfirmation: boolean /** toggle Require Email Confirmation */;
  sendEmailConfirmation: boolean /** toggle Send Email Confirmation */;
  registration_confirmation_email?: IScheduledEmailCustomizations;
  registration_email_settings?: IScheduledEmailSettings;
  registration_email_timezone?: string;
  postEventFeedbackSurvey: boolean;
  useBlockedDomainEmails: boolean;
  blockedDomainEmails: null | string[];
  requirements: RequirementMap;
  title?: TranslateString;
  description?: TranslateString;
  image?: string;
  logo?: string;
  style?: ERegistrationStyle;
  customPasscodeLabel?: TranslateString;
  customRejectedPasscodeMessage?: TranslateString;
  enableRecaptcha?: boolean;
  enableOverviewContent?: boolean;
  enableSpeakers?: boolean;
  isClosed?: boolean; // does not toggle registration off, but disables registration form for new attendees
  adminInPersonAttendeeModeEnabled?: boolean;
  emailLinkLimit?: number | null; // if null or undefined, email link can be used an unlimited number of times
  marketing_email_options?: MarketingOptInTypes | null;
  externalRegistration?: {
    isOn?: boolean;
    url?: string | null;
    isRSVP?: boolean;
  };
  registration_questions_translations?: IRegistrationQuestionTranslations;
  marked_attended_by_admin?: MarkedAttendedRegistration;
  skipEmailOnDuplicateRegistration?: boolean;
  registration_limit_is_on?: boolean;
  registration_limit?: number | null;
  password_gating_enabled?: boolean;
  magic_links_registration_passcode_list?: number | undefined; // passcode list to use for magic links registration
  /**
   * project_gate_type is the single source of truth for the project's gating type
   * Previously, projectd could have some types on at the same time, but as of BDEV-4266, projects can now only have one gate type on at a time, so this parameter should be used to determine which gate type is active
   * so that we do not have to wipe user data when switching types, as the old system would do.
   * */
  project_gate_type?: GateTypes | null;
  /**
   * Tracks which sub-mode the domain_email gate is in. Persists the user's tab
   * selection in the gating panel so we don't have to derive it heuristically
   * from data presence on reload. Only meaningful when project_gate_type is domain_email.
   */
  domain_gate_mode?: 'allow' | 'block';
  // New parameters must be optional or nullable
}
export interface RequirementMap {
  [GateTypes.domain_email]?: Requirement;
  [GateTypes.shared_passcode]?: Requirement;
  [GateTypes.passcode_list]?: Requirement[];
  [GateTypes.ticketing]?: Requirement;
  [GateTypes.custom_page_gating]?: Requirement;
}

export interface Requirement {
  gatedSessions?: Session['session'][];
  type: GateTypes;
  allowedDomains?: string[];
  sharedPasscode?: string;
  passcodeList?: number[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  passcodeLists?: any[]; // used in aws and api
  ticketing?: string[];
  editing?: boolean;
  id?: string;
  // New parameters must be optional or nullable
}

export enum GateTypes {
  domain_email = 1,
  shared_passcode,
  passcode_list,
  ticketing,
  custom_page_gating,
  password,
}

export enum RegistrationTypes {
  none,
  open,
  gated,
}

export enum SingleSignOnTypes {
  Google = 'Google',
  Facebook = 'Facebook',
  LinkedIn = 'LinkedIn',
  SAML = 'SAML',
  OIDC = 'OIDC',
  oAuth = 'oAuth',
  OAUTH2 = 'OAUTH2',
}

export enum SSOIntegrationProviderType {
  COGNITO = 'COGNITO',
  JACKSON = 'JACKSON',
}

export interface SingleSignOnSettings {
  isOn: boolean;
  displayTop: boolean;
  singleSignOns?: Array<string>;
  // New parameters must be optional or nullable
  rules?: SSOAttributeRule[];
}

export interface SSOAttributeRule {
  uuid: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: number;
  updatedBy?: number;
  enabled: boolean;
  type: SSOAttributeRuleType;
  mustMeetAllConditions: boolean;
  conditions: SSOAttributeRuleCondition[];
}

export enum SSOAttributeRuleType {
  ALLOW = 'ALLOW',
  DENY = 'DENY',
}

export interface SSOAttributeRuleCondition {
  attribute: string;
  operator: SSOAttributeRuleConditionOperator;
  value: string;
}

export enum SSOAttributeRuleConditionOperator {
  EQUALS = 'EQUALS',
  NOT_EQUALS = 'NOT_EQUALS',
  CONTAINS = 'CONTAINS',
  NOT_CONTAINS = 'NOT_CONTAINS',
  STARTS_WITH = 'STARTS_WITH',
  ENDS_WITH = 'ENDS_WITH',
  EXISTS = 'EXISTS',
  BOOLEAN_TRUE = 'BOOLEAN_TRUE',
  BOOLEAN_FALSE = 'BOOLEAN_FALSE',
}
export interface ERegistrationStyle {
  styling_overrides?: PageModule['styling_overrides'];
  layout?: RegistrationLayoutTypes;
  hero_size?: HeroSizes;
}

export type HeroSizes = 'small' | 'large';

export enum EHeroSizes {
  small = 'small',
  large = 'large',
}
export type RegistrationLayoutTypes =
  | 'form-left'
  | 'form-right'
  | 'above-the-fold-l'
  | 'above-the-fold-r'
  | 'hero-l'
  | 'hero-r'
  | 'landing-l'
  | 'landing-r'
  | 'modal';

export enum MarketingOptInTypes {
  singleOptIn = 'single-opt-in',
  doubleOptIn = 'double-opt-in',
  noOptIn = 'no-opt-in',
}

export interface IRegistrationQuestionTranslations {
  [questionId: number]: {
    name?: TranslateString;
    placeholder?: TranslateString;
    options?: {
      [optionId: number]: TranslateString;
    };
  };
}

export type MarkedAttendedRegistration = 'in-person' | '' | null;

export interface RegistrationQuestion {
  registration_question: number;
  channel: number;
  name: string;
  type: QuestionType;
  options: string[] | null;
  global: boolean;
  persistent: boolean;
  default_value: string | number | null;
  required?: boolean;
  name_translation?: TranslateString;
  options_translation?: TranslateString[];
  placeholder?: TranslateString;
  deleted?: boolean;
  is_required?: boolean;
  // New parameters must be optional or nullable
}

export enum QuestionType {
  text = 'text',
  textarea = 'textarea',
  checkbox = 'checkbox',
  select = 'select',
  numeric = 'numeric',
  // date = "date", For Future Use - this type should not be used until fully implemented
  email = 'email',
  multiselect = 'multiselect',
  customtext = 'customtext',
}

export interface RegistrationStep {
  type: RegistrationStepType;
  isOn: boolean;
  questions?: RegistrationQuestion[];
  ticketing?: TicketingSet[];
  title?: TranslateString;
  styling_override?: TextOverrides;
  // New parameters must be optional or nullable
}

export enum RegistrationStepType {
  general = 'general',
  profile = 'profile',
  avatar = 'avatar',
  ticketing = 'ticketing',
}

export interface TicketingSet {
  name: string;
  price?: number /** @deprecated */;
  currency?: string /** @deprecated */;
  prices?: GlobalPrice[];
  quantity: number;
  uuid: string;
  description?: string;
  sessions: number[];
  session_uuids: string[];
  name_translations?: TranslateString;
  description_translations?: TranslateString;
  // New parameters must be optional or nullable
}

export interface GlobalPrice {
  price: number;
  currency: string;
  multiplier: number;
  // New parameters must be optional or nullable
}

export interface PageGating {
  isOn: boolean;
  pageGates: PageGate[];
  // New parameters must be optional or nullable
}

export interface PageGate {
  page: CustomPage['custom_page'];
  passcodeLists: PasscodeList[];
  // New parameters must be optional or nullable
}

export interface PasscodeWorkingGateRquirement {
  type: GateTypes.passcode_list;
  passcodeList: number;
  gatedSessions: number[];
}
export interface PasscodeList extends Module {
  passcode_list?: number;
  uuid: string;
  name: string;
  max_uses: number;
  master_passcode: string | null;
  type: PASSCODE_LIST_TYPE;
  member_count: number;
  dma_id?: string | null;
  description?: string | null;
  cloned_source?: string | null;
  // New parameters must be optional or nullable
}

export enum PASSCODE_LIST_TYPE {
  EMAILS_ONLY = 0,
  PASSCODES_ONLY,
  EMAILS_AND_PASSCODES,
  MAGIC_LINKS,
  MAGIC_LINKS_REGISTRATION,
}

export enum REGISTRANT_FILTER_TYPE {
  ALL_PROFILES = 'all',
  REGISTERED = 'registered',
  ATTENDED = 'attended',
  NO_SHOWS = 'no_shows',
}

export interface RegistrantFilter {
  name: string;
  type: REGISTRANT_FILTER_TYPE;
  member_count: number;
}

export enum AUDIENCES_VIEW {
  LISTS = 'lists',
  REGISTRANTS = 'registrants',
}

export enum AUDIENCES_VIEW_PATH {
  LISTS = 'audiences/lists',
  REGISTRANTS = 'audiences/registrants',
}

export enum AUDIENCE_CARD_TYPE {
  ALL_PROFILES = 'all',
  REGISTERED = 'registered',
  ATTENDED = 'attended',
  NO_SHOWS = 'no_shows',
  PASSCODE_LIST = 'passcode_list',
}

export interface AudienceCardProps {
  audienceCardType: AUDIENCE_CARD_TYPE;
  name?: string;
  subHeader?: string;
  passcodeListId?: number;
  passcodeList?: PasscodeList;
  member_count?: string | number;
  created_date?: Date;
  /** The list is attached while the project's audience list gate is actively restricting access. */
  restrictsAccess?: boolean;
}

export interface AudienceAddProps {
  audienceType: AUDIENCE_CARD_TYPE;
  name?: string;
  description?: string | null;
  passcodeListId?: number;
  member_count?: string | number;
  project_uuid?: string;
  registrant_counts?: ProjectRegistrantCounts;
  cloned_source?: string | null;
}

export type AudienceListsActionInitiatedResponse = {
  message: string;
};
export type CloneAudienceListsResponse = {
  data: { eventUuid?: number };
};

export type UserStatus = 'registered' | 'attended' | 'no_shows';

export type TotalUsers = 'total';

export type ChannelRegistrantCounts = Record<string, ProjectRegistrantCounts>;
export type ProjectRegistrantCounts = Record<UserStatus | TotalUsers, number>;

export type ChannelRegistrantCountRow = {
  event_uuid: string;
  total: string;
  registered: string;
  attended: string;
  no_shows: string;
};

export type MergeAudienceListsResponse = {
  data: { eventUuid?: number };
};

export type UploadListCompletePayload = {
  data: { eventUuid: string; passcodeListId: number; failedEmailCount: number };
};

export type SocketOperationError = {
  data: { message: string };
};

export type SelectedMergeAudienceLists = {
  sourceAudienceLists?: number[];
  sourceRegistrantLists?: string[];
  targetAudienceList?: number;
};

export enum RegFieldsEnum {
  first_name = 1,
  last_name = 2,
  email = 3,
  bio = 4,
  job_title = 5,
  company_name = 6,
  location = 7,
  store_name = 8,
  fun_fact = 9,
  passcode = 10,
  comments = 11,
  attend_in_person = 12,
  interests = 17,
  state = 18,
  postal_code = 21,
  phone_number = 38,
  age = 47,
  department = 89,
  industry = 103,
  title = 92,
  country = 1184,
}

export interface IUpdateRegStepsOptions {
  makeNameOptional?: boolean;
  requiredQuestions?: number[];
  registrationQuestionsToAppend?: {
    step: RegistrationStepType;
    questions: RegistrationQuestion[];
  };
}

export interface PasscodeListMember {
  passcode_list_member: number;
  passcode_list: number;
  passcode: string | null;
  created_date: string;
  imported_profile: Record<string, string> | null;
  email: string;
  uses: number;
  avatar?: string;
  last_login?: string;
  registrations_count?: number;
  attended_count?: number;
  is_sent?: boolean;
  magic_link?: MagicLink;
}

export interface AdditionalRegistrantDetails {
  email: string;
  avatar?: string;
  last_login?: string;
}

export const PASSCODE_LIST_MEMBER_PAGE_SIZE = 50;

export const REGISTRANT_LIST_PAGE_SIZE = 50;

export type DownloadStringValues = 'NORMAL' | 'SPECIFIC' | 'SPECIFIC_MAGIC' | 'NORMAL_MAGIC';

export enum DownloadCSVStringValues {
  NORMAL = 'NORMAL',
  SPECIFIC = 'SPECIFIC',
  SPECIFIC_MAGIC = 'SPECIFIC_MAGIC',
  NORMAL_MAGIC = 'NORMAL_MAGIC',
}

export interface IPaidList {
  ticketingSet: TicketingSet;
  eventName: string;
  eventUuid: string;
  members: number;
  sessionNames: string[];
  created_at?: Date;
}

export interface PaidListsResponse {
  [key: string]: IPaidList; // key is the paid list UUID
}

export interface TicketSetMember {
  succeeded: boolean;
  email: string;
  created: string;
  event: number;
  currency: string;
  amount: number;
  fee_amount: number;
  ticket_uuid: number;
}

export interface ExchangeResponse {
  base: string;
  last_updated: number;
  conversion_rates: { [country_code: string]: number };
  // New parameters must be optional or nullable
}

type SSODefaultAttributeMapping = {
  email: string;
  given_name: string;
  family_name: string;
  avatar?: string;
};

type SSOCustomRegFieldMapping = Record<`reg_question.${number}`, string>;

export type SSOAttributeMapping = SSODefaultAttributeMapping & SSOCustomRegFieldMapping;

export interface ISSOIntegration {
  uuid: string;
  channel: number;
  idp_name: string;
  enabled: boolean;
  type: SingleSignOnTypes;
  icon: string;
  login_text: string;
  user_pool_client_id: string;
  integration_provider?: SSOIntegrationProviderType;
  allow_non_email_identifier?: boolean;
  // New parameters must be optional or nullable
}

export interface ICreateSamlIntegration {
  channel: number;
  enabled: boolean;
  type: SingleSignOnTypes.SAML;
  login_text: string;
  icon: string;
  xml_url: string;
  attribute_mapping: SSOAttributeMapping;
  admin_login?: boolean;
  // not from table, extended onto this type to handle testing the
  // SSO integration from the integrations page in admin console
  // as these are added onto the type and we didn't catch it because of the any type
  // and I didn't think it necessary to refactor the entire thing right now.

  // TODO: update type on the saml-integrations.tsx file to handle the types as expected
  idp_name?: string;
  user_pool_client_id?: string;
  allow_non_email_identifier?: boolean;
  // New parameters must be optional or nullable
}

export interface SamlIntegration extends ICreateSamlIntegration {
  uuid: string;
  created_by: number;
  created_at: string;
  modified_by: number;
  modified_at: string;
  // New parameters must be optional or nullable
}

export type SSOValue = {
  disabled: boolean;
  toggled: boolean;
};

export type JacksonSSOOptionstype = SamlIntegration | OIDCIntegration;
export type SSOOptionsType = JacksonSSOOptionstype | Oauth2Integration;

export type SSOStatus = {
  [SingleSignOnTypes.SAML]: SSOValue;
  [SingleSignOnTypes.OIDC]: SSOValue;
  [SingleSignOnTypes.OAUTH2]: SSOValue;
};

export interface ICreateOIDCIntegration {
  channel: number;
  enabled: boolean;
  type: SingleSignOnTypes.OIDC;
  login_text: string;
  icon: string;
  client_id: string;
  oidc_issuer: string;
  client_secret?: string;
  attribute_mapping: SSOAttributeMapping;
  // not from table, extended onto this type to handle testing the
  // SSO integration from the integrations page in admin console
  // as these are added onto the type and we didn't catch it because of the any type
  // and I didn't think it necessary to refactor the entire thing right now.

  // TODO: update type on the oidc-integrations.tsx file to handle the types as expected
  idp_name?: string;
  user_pool_client_id?: string;
  admin_login?: boolean;
  allow_non_email_identifier?: boolean;
  // New parameters must be optional or nullable
  integration_provider?: SSOIntegrationProviderType;
}

export interface OIDCIntegration extends ICreateOIDCIntegration {
  uuid: string;
  created_by: number;
  created_at: string;
  modified_by: number;
  modified_at: string;
  // New parameters must be optional or nullable
}

export enum EOauthAttributes {
  first_name = 1,
  last_name,
  email,
}

export interface CreateOauth2Integration {
  channel: number;
  active: boolean;
  client_id: string;
  client_secret: string;
  authorization_server_url: string;
  token_server_url: string;
  resource_server_url: string | null;
  login_text: string | null;
  attribute_mapping: Record<EOauthAttributes, string>;
  scopes: string[];
  icon: string | null;
  query_params: Record<string, string>;
  logout_url: string | null;
  admin_login?: boolean;
  // New parameters must be optional or nullable
}

export interface Oauth2Integration extends CreateOauth2Integration {
  uuid: string;
  created_by: number;
  created_at: string;
  modified_by: number;
  modified_at: string;
  // New parameters must be optional or nullable
}
